<template>
  <div class="animate-fade-in">
    <div class="glass rounded-xl p-6">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl text-white font-semibold">{{ monthLabel }}</h3>
        <div class="space-x-2">
          <button
            @click="emit('prev')"
            class="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-white"
          >
            Prev
          </button>
          <button
            @click="emit('next')"
            class="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-white"
          >
            Next
          </button>
        </div>
      </div>
      <div class="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div class="flex flex-wrap items-center gap-3">
          <span class="inline-flex items-center gap-1"
          ><span
            class="w-3 h-3 rounded bg-green-500/25 border border-green-500/40 inline-block"
          ></span
          ><span class="text-slate-300">Available</span></span
        >
        <span class="inline-flex items-center gap-1"
          ><span
            class="w-3 h-3 rounded bg-amber-500/25 border border-amber-500/40 inline-block"
          ></span
          ><span class="text-slate-300">Reserved</span></span
        >
        <span class="inline-flex items-center gap-1"
          ><span
            class="w-3 h-3 rounded bg-blue-500/25 border border-blue-500/40 inline-block"
          ></span
          ><span class="text-slate-300">Checked Out</span></span
        >
        <span class="inline-flex items-center gap-1"
          ><span class="w-3 h-3 rounded bg-red-500/25 border border-red-500/40 inline-block"></span
          ><span class="text-slate-300">Maintenance</span></span
        >
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <button
            v-if="displayMode === 'detailed'"
            type="button"
            class="px-2.5 py-1 rounded border border-slate-700/80 bg-slate-900/60 text-[11px] font-medium text-slate-200 hover:text-white hover:bg-slate-800/80 transition-colors"
            @click="toggleExpandAll"
          >
            {{ expandAllDays ? 'Collapse all' : 'Expand all' }}
          </button>
          <div class="inline-flex items-center gap-2 rounded border border-slate-700/80 bg-slate-900/60 p-1">
            <button
              class="px-2.5 py-1 rounded text-[11px] font-medium transition-colors"
              :class="
                displayMode === 'detailed'
                  ? 'bg-primary-600 text-white'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/80'
              "
              @click="setDisplayMode('detailed')"
            >
              Detailed
            </button>
            <button
              class="px-2.5 py-1 rounded text-[11px] font-medium transition-colors"
              :class="
                displayMode === 'compact'
                  ? 'bg-primary-600 text-white'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/80'
              "
              @click="setDisplayMode('compact')"
            >
              Compact
            </button>
          </div>
          <label class="inline-flex items-center gap-1.5 text-[11px] text-slate-300 select-none">
            <input
              v-model="usePreferredView"
              type="checkbox"
              class="accent-primary-500"
              @change="onPreferredViewChange"
            />
            My preferred view
          </label>
        </div>
      </div>
      <div class="grid grid-cols-7 gap-2">
        <div
          v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']"
          :key="day"
          class="p-2 text-center text-slate-400 text-sm font-medium"
        >
          {{ day }}
        </div>
        <div
          v-for="cell in cells"
          :key="cell.key"
          :class="[
            'relative p-3 min-h-[72px] border border-slate-700 rounded-lg tilt-light',
            cell.isToday
              ? 'bg-primary-600 text-white'
              : cell.inCurrentMonth
                ? 'hover:bg-slate-800 text-white'
                : 'bg-slate-900 text-slate-500',
            'transition-transform transition-colors cursor-pointer',
          ]"
          @mousedown="onPress"
          @mouseup="onRelease"
          @mouseleave="onRelease"
        >
          <div class="text-sm font-medium">{{ cell.date.getDate() }}</div>
          <div v-if="displayMode === 'compact'" class="mt-1 space-y-1">
            <div class="flex flex-wrap gap-1.5">
              <span
                v-if="getStatusCount(cell.date, 'reserved') > 0"
                class="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/20 px-1.5 py-[2px] text-[10px] text-amber-100"
              >
                <span class="inline-block h-1.5 w-1.5 rounded-full bg-amber-300"></span>
                {{ getStatusCount(cell.date, 'reserved') }}
              </span>
              <span
                v-if="getStatusCount(cell.date, 'checked-out') > 0"
                class="inline-flex items-center gap-1 rounded border border-blue-500/40 bg-blue-500/20 px-1.5 py-[2px] text-[10px] text-blue-100"
              >
                <span class="inline-block h-1.5 w-1.5 rounded-full bg-blue-300"></span>
                {{ getStatusCount(cell.date, 'checked-out') }}
              </span>
              <span
                v-if="getStatusCount(cell.date, 'maintenance') > 0"
                class="inline-flex items-center gap-1 rounded border border-red-500/40 bg-red-500/20 px-1.5 py-[2px] text-[10px] text-red-100"
              >
                <span class="inline-block h-1.5 w-1.5 rounded-full bg-red-300"></span>
                {{ getStatusCount(cell.date, 'maintenance') }}
              </span>
            </div>
          </div>
          <div v-else class="mt-1 space-y-1">
            <div
              v-for="r in getVisibleReservations(cell.date)"
              :key="r.id || r.equipment_id"
              :class="[
                'text-[10px] px-2 py-1 rounded border truncate cursor-pointer',
                r._isStart && !r._isEnd ? 'rounded-r-none' : '',
                r._isEnd && !r._isStart ? 'rounded-l-none' : '',
                normalizeStatus(r.status) === 'reserved'
                  ? 'bg-amber-500/25 border-amber-500/40'
                  : '',
                normalizeStatus(r.status) === 'checked-out'
                  ? 'bg-blue-500/25 border-blue-500/40'
                  : '',
                normalizeStatus(r.status) === 'maintenance'
                  ? 'bg-red-500/25 border-red-500/40'
                  : '',
              ]"
              @click="emit('open-reservation', r)"
            >
              {{ r.equipment_serial || '#' + r.equipment_id }}
            </div>
            <button
              v-if="getDayReservations(cell.date).length > 2"
              type="button"
              class="text-[10px] text-slate-300 hover:text-white underline underline-offset-2"
              @click.stop="toggleOverflowForDay(cell.date)"
            >
              {{
                expandedDayKey === cell.date.toDateString()
                  ? 'Show less'
                  : `+${getOverflowCount(cell.date)} more`
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Placeholder for reservation bars (to be sourced from API) -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

type CalendarCell = { key: string; date: Date; inCurrentMonth: boolean; isToday: boolean }
type Reservation = {
  id: number
  equipment_id: number
  purpose: string
  _isStart?: boolean
  _isEnd?: boolean
} & Record<string, unknown>

const props = defineProps<{
  monthLabel: string
  cells: CalendarCell[]
  reservationsByDay: Record<string, Reservation[]>
}>()

const emit = defineEmits<{
  (e: 'prev'): void
  (e: 'next'): void
  (e: 'open-reservation', r: Reservation): void
}>()

const CALENDAR_PREFERRED_VIEW_KEY = 'testdrive.calendar.preferredView'
const displayMode = ref<'compact' | 'detailed'>('detailed')
const expandedDayKey = ref<string | null>(null)
const expandAllDays = ref(false)
const usePreferredView = ref(false)

function normalizeStatus(rawStatus: unknown) {
  return String(rawStatus || '')
    .toLowerCase()
    .replace(/_/g, '-')
}

function getDayReservations(date: Date) {
  const key = date.toDateString()
  return (props.reservationsByDay[key] || []).filter((reservation) => {
    const status = normalizeStatus(reservation.status)
    return status === 'reserved' || status === 'checked-out' || status === 'maintenance'
  })
}

function getVisibleReservations(date: Date) {
  const key = date.toDateString()
  const reservations = getDayReservations(date)
  return expandAllDays.value || expandedDayKey.value === key ? reservations : reservations.slice(0, 2)
}

function getOverflowCount(date: Date) {
  const total = getDayReservations(date).length
  const visible = getVisibleReservations(date).length
  return Math.max(0, total - visible)
}

function getStatusCount(date: Date, targetStatus: 'reserved' | 'checked-out' | 'maintenance') {
  return getDayReservations(date).filter((reservation) => normalizeStatus(reservation.status) === targetStatus).length
}

function toggleOverflowForDay(date: Date) {
  if (expandAllDays.value) return
  const key = date.toDateString()
  expandedDayKey.value = expandedDayKey.value === key ? null : key
}

function setDisplayMode(mode: 'compact' | 'detailed') {
  displayMode.value = mode
  if (mode === 'compact') {
    expandedDayKey.value = null
    expandAllDays.value = false
  }
  if (usePreferredView.value) {
    savePreferredView(mode)
  }
}

function toggleExpandAll() {
  if (displayMode.value !== 'detailed') return
  expandAllDays.value = !expandAllDays.value
  if (expandAllDays.value) {
    expandedDayKey.value = null
  }
}

function savePreferredView(mode: 'compact' | 'detailed') {
  try {
    localStorage.setItem(CALENDAR_PREFERRED_VIEW_KEY, mode)
  } catch {
    // Ignore storage issues in restricted browser contexts.
  }
}

function clearPreferredView() {
  try {
    localStorage.removeItem(CALENDAR_PREFERRED_VIEW_KEY)
  } catch {
    // Ignore storage issues in restricted browser contexts.
  }
}

function onPreferredViewChange() {
  if (usePreferredView.value) {
    savePreferredView(displayMode.value)
    return
  }
  clearPreferredView()
}

function onPress(e: Event) {
  const el = e.currentTarget as HTMLElement | null
  if (el) el.style.transform = 'scale(0.98)'
}

function onRelease(e: Event) {
  const el = e.currentTarget as HTMLElement | null
  if (el) el.style.transform = 'scale(1)'
}

onMounted(() => {
  try {
    const saved = localStorage.getItem(CALENDAR_PREFERRED_VIEW_KEY)
    if (saved === 'compact' || saved === 'detailed') {
      displayMode.value = saved
      usePreferredView.value = true
      if (saved === 'compact') {
        expandAllDays.value = false
        expandedDayKey.value = null
      }
    }
  } catch {
    // Ignore storage issues in restricted browser contexts.
  }
})
</script>
